import type { IFarm } from 'interfaces/IFarm';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IInput, IUnit } from 'interfaces/IInput';
import {
  InputStepType,
  type IInputStepProduction,
  type IInputStepSeed,
} from 'interfaces/IInputStep';
import {
  InputTaskCategory,
  InputTaskType,
  type IInputTask,
} from 'interfaces/IInputTask';
import {
  getInputYear,
  indexInputsByPaddock,
  sortInputsByStartDateAscending,
} from 'utils/FarmUtils';
import { convertToHectares, convertToTonnes } from 'utils/unitConversion';

import { resolveAreaHa } from 'pages/FarmsPage/Workspace/Insights/insightUtils';
import { seedMatchesPlanCrop } from 'pages/FarmsPage/Workspace/Tasks/planCropDefaults';

/** One seed in a seed mix — a Seed step with its own crop and variety. */
export interface RotationMixSeed {
  cropName: string;
  cropId: string | null;
  varietyName: string | null;
  varietyId: string | null;
  rate: number | null;
  unit: IUnit | null;
  cost: number | null;
}

/** A plan's one sowing record in the selected mode — the values a cell shows and edits. */
export interface RotationSowing {
  taskId: string;
  /** The task's Seed step, or null when the task has none yet or sows a mix. */
  stepId: string | null;
  rate: number | null;
  unit: IUnit | null;
  /** Cost per unit of the sowing rate, e.g. $/kg for kg/ha. */
  cost: number | null;
  /** The sowing date — the task's date, as the Tasks grid shows it. */
  date: string | null;
  dateHasDay: boolean;
  /**
   * The seeds when the task sows a mix (two or more Seed steps, e.g. a
   * pasture blend), in step order; rate, unit, cost and stepId are then
   * null. Null for one seed or none.
   */
  mix: RotationMixSeed[] | null;
}

/**
 * How many sowing records (crop establishment tasks) a plan has in the
 * selected mode. One task maps onto a cell, whatever seeds it sows; more
 * than one is edited in Planning.
 */
export type RotationSowingState = 'none' | 'single' | 'multiple';

/** How the cell's Plan sowing is made up: one seed (or none), a seed mix, or several tasks. */
export const rotationSowingKind = (
  cell: Pick<RotationCell, 'sowingState' | 'sowing'>
): 'single' | 'mix' | 'multiple' => {
  if (cell.sowingState === 'multiple') return 'multiple';
  return cell.sowing?.mix ? 'mix' : 'single';
};

/** A Plan sowing record whose crop or variety isn't the plan's. */
export interface RotationSowingMismatch {
  cropName: string;
  varietyName: string | null;
}

/** One plan in one season, as a grid cell shows it. */
export interface RotationCell {
  inputId: string;
  season: number;
  cropName: string | null;
  cropId: string | null;
  varietyName: string | null;
  varietyId: string | null;
  /** The plan's own area (ha), or null when it has none. */
  areaHa: number | null;
  /** The plan's area as entered, in areaUnit — shown in the cell. */
  areaValue: number | null;
  /** The plan's area unit as entered (e.g. "ha", "ac") — rates are per this unit. */
  areaUnit: string | null;
  /** True when the plan has an Actual sowing record — a paste never changes such a plan. */
  hasActualSowing: boolean;
  /** True when the plan has tasks other than its Plan sowing record, which a paste keeps. */
  hasOtherTasks: boolean;
  /** True when the plan has any Actual task — deleting the plan deletes those records too. */
  hasActualTasks: boolean;
  /** The Plan sowing record — what the editor and a paste change. */
  sowingState: RotationSowingState;
  /** Set when sowingState is 'single'. */
  sowing: RotationSowing | null;
  /**
   * The first Plan sowing record sown with a different crop or variety from
   * the plan's (e.g. after a change in Planning), or null when they agree.
   */
  sowingMismatch: RotationSowingMismatch | null;
  /** Which category the cell shows: Plan, or Actual for a past year when chosen. */
  shownCategory: InputTaskCategory;
  /** The sowing record in shownCategory, for the cell's caption. */
  shownSowingState: RotationSowingState;
  shownSowing: RotationSowing | null;
  /** Production in shownCategory, or null when none has a yield. */
  yieldTPerHa: number | null;
  productionT: number | null;
}

export interface RotationPaddockRow {
  paddockId: string;
  paddockName: string;
  areaHa: number | null;
  /** The paddock's area unit as entered, for a plan not yet created. */
  areaUnit: string | null;
  zoneId: string | null;
  /** How many rows the paddock takes — its most plans in any one season, at least one. */
  lanes: number;
  /** Plans by season, in start-date order. A season with none has an empty list. */
  cells: Record<number, RotationCell[]>;
}

export interface RotationFarmGroup {
  farmId: string;
  farmName: string;
  areaHa: number;
  paddocks: RotationPaddockRow[];
}

export interface RotationGrid {
  farms: RotationFarmGroup[];
  /** True when any paddock in scope has a plan in the seasons shown. */
  hasAnyPlan: boolean;
}

/** Which paddocks the grid lists — every visible paddock, one farm's, or one paddock. */
export type RotationScope =
  | { level: 'org' }
  | { level: 'farm'; farmId: string }
  | { level: 'paddock'; paddockId: string };

interface ComputeRotationGridArgs {
  /** Visible farms only (useVisibleOrgData). */
  farms: IFarm[];
  /** Plans across every season; only those in `seasons` are placed. */
  inputs: IInput[];
  seasons: number[];
  /** Years before this show pastCategory; this year and later show Plan. */
  planningSeason: number;
  pastCategory: InputTaskCategory;
  scope: RotationScope;
  gUnits: GUnit[];
}

const toHa = (
  value: number | null | undefined,
  unit: IUnit | null | undefined,
  gUnits: GUnit[]
): number | null => {
  if (!value || value <= 0) return null;
  return convertToHectares(value, unit, gUnits) ?? value;
};

const tasksOf = (
  input: IInput,
  taskType: InputTaskType,
  category: InputTaskCategory
): IInputTask[] =>
  (input.tasks ?? []).filter(
    (task) => task.taskType === taskType && task.category === category
  );

const resolveSowing = (
  input: IInput,
  category: InputTaskCategory
): Pick<RotationCell, 'sowingState' | 'sowing'> => {
  const tasks = tasksOf(input, InputTaskType.CropEstablishment, category);
  if (tasks.length === 0) return { sowingState: 'none', sowing: null };
  if (tasks.length > 1) return { sowingState: 'multiple', sowing: null };

  const [task] = tasks;
  const seeds = (task.steps ?? []).filter(
    (step): step is IInputStepSeed => step.stepType === InputStepType.Seed
  );
  const mix: RotationMixSeed[] | null =
    seeds.length > 1
      ? seeds.map((step) => ({
          cropName: step.item,
          cropId: step.globalId ?? null,
          varietyName: step.item2 ?? null,
          varietyId: step.globalId2 ?? null,
          rate: step.rate ?? null,
          unit: step.appUnit ?? null,
          cost: step.cost ?? null,
        }))
      : null;
  const seed = mix ? null : (seeds[0] ?? null);
  return {
    sowingState: 'single',
    sowing: {
      taskId: task.taskId,
      stepId: seed?.stepId ?? null,
      rate: seed?.rate ?? null,
      unit: seed?.appUnit ?? null,
      cost: seed?.cost ?? null,
      date: task.endDate,
      dateHasDay: task.endDateHasDay,
      mix,
    },
  };
};

/**
 * The first Plan Seed step whose crop or variety isn't the plan's — the same
 * rule Planning alerts on (seedMatchesPlanCrop, OP-2536).
 */
export const findSowingMismatch = (
  input: IInput
): RotationSowingMismatch | null => {
  const crop = input.crop;
  if (!crop?.cropName) return null;
  for (const task of tasksOf(
    input,
    InputTaskType.CropEstablishment,
    InputTaskCategory.Plan
  )) {
    for (const step of task.steps ?? []) {
      if (step.stepType !== InputStepType.Seed) continue;
      const seed = step as IInputStepSeed;
      if (!seedMatchesPlanCrop(seed, crop)) {
        return { cropName: seed.item, varietyName: seed.item2 ?? null };
      }
    }
  }
  return null;
};

/** Total tonnes over every production in the mode, and that as t/ha of the plan's area. */
const resolveProduction = (
  input: IInput,
  category: InputTaskCategory,
  planAreaHa: number | null,
  gUnits: GUnit[]
): Pick<RotationCell, 'yieldTPerHa' | 'productionT'> => {
  let productionT = 0;
  let hasYield = false;
  tasksOf(input, InputTaskType.Production, category).forEach((task) => {
    (task.steps ?? []).forEach((step) => {
      if (step.stepType !== InputStepType.Production) return;
      const production = step as IInputStepProduction;
      const yieldRaw = production.yield ?? 0;
      if (yieldRaw <= 0) return;
      const areaHa = resolveAreaHa(step, task, input, gUnits);
      if (areaHa <= 0) return;
      const tPerHa =
        convertToTonnes(yieldRaw, production.yieldUnit, gUnits) ?? yieldRaw;
      productionT += tPerHa * areaHa;
      hasYield = true;
    });
  });

  if (!hasYield) return { yieldTPerHa: null, productionT: null };
  return {
    productionT,
    yieldTPerHa: planAreaHa ? productionT / planAreaHa : null,
  };
};

export const toRotationCell = (
  input: IInput,
  season: number,
  shownCategory: InputTaskCategory,
  gUnits: GUnit[]
): RotationCell => {
  const areaHa = toHa(input.zone?.zoneArea, input.zone?.areaUnit, gUnits);
  const tasks = input.tasks ?? [];
  const isPlanSowing = (task: IInputTask) =>
    task.taskType === InputTaskType.CropEstablishment &&
    task.category === InputTaskCategory.Plan;
  return {
    inputId: input.id,
    season,
    cropName: input.crop?.cropName?.trim() || null,
    cropId: input.crop?.globalId ?? null,
    varietyName: input.crop?.variety?.varietyName?.trim() || null,
    varietyId: input.crop?.variety?.globalId ?? null,
    areaHa,
    areaValue:
      input.zone?.zoneArea && input.zone.zoneArea > 0
        ? input.zone.zoneArea
        : null,
    areaUnit: input.zone?.areaUnit?.unit?.trim() || null,
    hasActualSowing: tasks.some(
      (task) =>
        task.taskType === InputTaskType.CropEstablishment &&
        task.category === InputTaskCategory.Actual
    ),
    hasOtherTasks: tasks.some((task) => !isPlanSowing(task)),
    hasActualTasks: tasks.some(
      (task) => task.category === InputTaskCategory.Actual
    ),
    ...resolveSowing(input, InputTaskCategory.Plan),
    sowingMismatch: findSowingMismatch(input),
    shownCategory,
    ...(() => {
      const shown = resolveSowing(input, shownCategory);
      return { shownSowingState: shown.sowingState, shownSowing: shown.sowing };
    })(),
    ...resolveProduction(input, shownCategory, areaHa, gUnits),
  };
};

const isInScope = (
  scope: RotationScope,
  farmId: string,
  paddockId: string
): boolean => {
  if (scope.level === 'farm') return farmId === scope.farmId;
  if (scope.level === 'paddock') return paddockId === scope.paddockId;
  return true;
};

/**
 * The Rotation planner grid (OP-2501): every paddock in scope as a row,
 * grouped by farm, with its plans placed by season. A plan's season is the
 * year its start date falls in, so a plan that doesn't start on 1 January
 * still lands in its own column. A season with two plans gives the paddock a
 * second row (lane) for the second, in start-date order.
 *
 * Crop and variety come from the plan. Sowing and production shown in a
 * cell come from Plan, or for a year before the planning year from Actual
 * when chosen — one category only, never mixed. The cell's Plan sowing is
 * kept separately, as that's what editing and pasting change.
 */
export const computeRotationGrid = ({
  farms,
  inputs,
  seasons,
  planningSeason,
  pastCategory,
  scope,
  gUnits,
}: ComputeRotationGridArgs): RotationGrid => {
  const seasonSet = new Set(seasons);
  const byPaddock = indexInputsByPaddock(inputs);
  let hasAnyPlan = false;

  const farmGroups: RotationFarmGroup[] = [];
  farms.forEach((farm) => {
    const paddocks: RotationPaddockRow[] = [];
    (farm.paddocks ?? []).forEach((paddock) => {
      if (paddock.isExcluded) return;
      if (!isInScope(scope, farm.id, paddock.paddockId)) return;

      const cells: Record<number, RotationCell[]> = {};
      seasons.forEach((season) => {
        cells[season] = [];
      });
      sortInputsByStartDateAscending(
        byPaddock.get(paddock.paddockId) ?? []
      ).forEach((input) => {
        const season = getInputYear(input);
        if (season === null || !seasonSet.has(season)) return;
        const shownCategory =
          season < planningSeason ? pastCategory : InputTaskCategory.Plan;
        cells[season].push(
          toRotationCell(input, season, shownCategory, gUnits)
        );
        hasAnyPlan = true;
      });

      paddocks.push({
        paddockId: paddock.paddockId,
        paddockName: paddock.paddockName,
        areaHa: toHa(paddock.paddockArea, paddock.areaUnit, gUnits),
        areaUnit: paddock.areaUnit?.unit?.trim() || null,
        zoneId: paddock.zoneId || null,
        lanes: Math.max(1, ...seasons.map((season) => cells[season].length)),
        cells,
      });
    });

    if (paddocks.length === 0) return;
    paddocks.sort((a, b) =>
      a.paddockName.localeCompare(b.paddockName, undefined, { numeric: true })
    );
    farmGroups.push({
      farmId: farm.id,
      farmName: farm.farmName,
      areaHa: paddocks.reduce((total, p) => total + (p.areaHa ?? 0), 0),
      paddocks,
    });
  });

  farmGroups.sort((a, b) =>
    a.farmName.localeCompare(b.farmName, undefined, { numeric: true })
  );
  return { farms: farmGroups, hasAnyPlan };
};

/**
 * `count` seasons from `firstSeason`, oldest first — calendar years, so a
 * gap year still gets its column.
 */
export const resolveRotationSeasons = (
  firstSeason: number,
  count: number
): number[] => Array.from({ length: count }, (_, index) => firstSeason + index);
